import { ButtonHeader } from '../../../Header/Style';
import { Control, ControlContainer } from '../../../Controls/styled';
import SVG from '../../../SVG';
import {
  downloadExcel,
  getFilterColumn,
  getSortColumn,
  jsonToExcel,
  setFilterColumn,
  setSortColumn,
} from '../../../../store/utils';
import Table from '../../../Table';
import useExportsController from '../useExportsController';
import { useState } from 'react';
import { formatBRL, formatCompleteBRL } from '../../../../store/masks';
import { useAbility } from '../../../Hooks/useAbility';

const useInvoicesController = () => {
  const {
    getInvoice,
    setInvoice,
    validateForm,
    getFormMessage,
    enqueueSnackbar,
    deleteInvoices: deleteInvoicesAction,
    autocompleteInvoice,
    filterInvoice,
    createInvoice,
    updateInvoice,
    setModal,
    currentRoute,
    filterSelectTaxes,
    selectCompanies,
    setCompanyFilterField,
    filterSelectCompanies,
    filterInvoiceProduct,
    filterCompleteInvoice,
    updateLockInvoice,
    filterGroupInvoice,
    groups,
    user,
    selectedGroupId,
  } = useExportsController();
  const { Can } = useAbility();

  const taxFilter = getInvoice('filters.selectTaxes.filter');

  const setTaxFilter = (filter: SearchFilter) =>
    setInvoice('filters.selectTaxes.filter', filter);

  const setTaxFilterField = (field: string, operator?: OPERATOR) => (e) => {
    let filter = setFilterColumn(field, e.target.value, taxFilter, operator);
    setTaxFilter({
      ...filter,
      page: 1,
    });
  };

  const invoices = getInvoice('filters.invoices.data.data');
  const checkInvoiceIds = getInvoice('selecteds.invoices');
  const reportInvoice = getInvoice('filters.invoices.data.report');

  const setCheckInvoiceIds = (selectedInvoiceIds: number[]) =>
    setInvoice('selecteds.invoices', selectedInvoiceIds);

  const allInvoiceChecks = invoices.every(({ id }) =>
    checkInvoiceIds.includes(id),
  );

  const invoiceCheckAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    if (checked) {
      setCheckInvoiceIds(invoices.map(({ id }) => id));
    } else {
      setCheckInvoiceIds([]);
    }
  };

  const checkInvoiceId = (invoiceId: number) => {
    const newcheckIds = checkInvoiceIds.includes(invoiceId)
      ? checkInvoiceIds.filter((id) => id !== invoiceId)
      : [...checkInvoiceIds, invoiceId];
    setCheckInvoiceIds(newcheckIds);
  };

  const deleteInvoiceLoadings = getInvoice('loadings.deleteInvoices');

  const setDeleteInvoiceLoadings = (setDeleteInvoiceLoadings: boolean[]) =>
    setInvoice('loadings.deleteInvoices', setDeleteInvoiceLoadings);

  const deleteInvoices = (invoiceIds: number[] = checkInvoiceIds) =>
    new Promise((resolve, reject) => {
      {
        const length = invoiceIds.length;

        const indexes = invoiceIds.map((invoiceId) =>
          invoices.findIndex(({ id }) => invoiceId === id),
        );

        setDeleteInvoiceLoadings(
          invoices.map((_, i) =>
            indexes.includes(i) ? true : deleteInvoiceLoadings[i],
          ),
        );

        deleteInvoicesAction(invoiceIds)
          .then(() => {
            const newCheckIds = checkInvoiceIds.filter(
              (id) => !invoiceIds.includes(id),
            );
            setCheckInvoiceIds(newCheckIds);
            filterInvoice();
            resolve(true);
            enqueueSnackbar(
              length === 1
                ? 'A invoice foi excluída com sucesso'
                : 'As invoices foram excluídas com sucesso',
              {
                variant: 'success',
              },
            );
          })
          .catch(() => {
            reject(false);
            enqueueSnackbar(
              length === 1
                ? 'Houve uma falha ao excluir a invoice'
                : 'Houve uma falha ao excluir as invoices',
              {
                variant: 'error',
              },
            );
          })
          .finally(() => {
            setDeleteInvoiceLoadings(
              invoices.map((_, i) =>
                indexes.includes(i) ? false : deleteInvoiceLoadings[i],
              ),
            );
          });
      }
    });

  const editInvoice = ({
    id,
    product,
    date,
    crt,
    invoiceNumber,
    invoiceValue,
    invoiceQuantity,
    unitCostReal,
    realValue,
    status,
    totalCost,
    unitCostTotal,
    unitSale,
    unitProfit,
    totalProfit,
    company,
    group,
    invoiceTaxes,
    labeled,
    observation,
    locked,
  }: Invoice) => {
    setModal('single.updateInvoice', true);
    setInvoice('forms.invoices.update', {
      id,
      product,
      date,
      crt,
      invoiceNumber,
      invoiceValue,
      invoiceQuantity,
      unitCostReal,
      realValue,
      status,
      totalCost,
      unitCostTotal,
      unitSale,
      unitProfit,
      totalProfit,
      observation: observation ?? '',
      company: {
        id: company.id,
        name: company.name,
      },
      invoiceTaxes: invoiceTaxes.map(({ id, value, tax }) => ({
        id,
        value,
        tax: {
          id: tax.id,
          name: tax.name,
          value: tax.value,
          description: tax.description,
        },
      })),
      locked,
      labeled,
      group: {
        id: group?.id ?? 0,
        number: group?.number ?? 0,
        name: group?.name ?? ''
      }
    });
  };

  const searchFilter = getInvoice('filters.invoices.filter');
  const setSearchFilter = (filter: SearchFilter) =>
    setInvoice('filters.invoices.filter', filter);

  const getFilterField = (field: string, operator?: OPERATOR) =>
    getFilterColumn(field, searchFilter, operator);

  const setFilterField = (field: string, operator?: OPERATOR) => (e) => {
    let filter = setFilterColumn(field, e.target.value, searchFilter, operator);
    setSearchFilter({
      ...filter,
      page: 1,
    });
  };

  const getSortField = (field: string) => getSortColumn(field, searchFilter);
  const setSortField = (field: string) => (value) =>
    setSearchFilter(setSortColumn(field, value, searchFilter));

  const getAutocompleteItems = <
    T extends keyof InvoiceState['autocompletes']['invoices'],
  >(
    field: T,
  ) =>
    getInvoice(`autocompletes.invoices.${field}.data`).map((name, id) => ({
      id,
      name,
    }));

  const setAutocompleteItems = (
    field: keyof InvoiceState['autocompletes']['invoices'],
  ) => {
    return autocompleteInvoice(field);
  };

  const setAutocompleteFilterField =
    (field: keyof InvoiceState['autocompletes']['invoices']) => (e) => {
      setInvoice(
        `autocompletes.invoices.${field}.filter.value`,
        e.target.value,
      );
    };

  const setField = (field: any, operator?: OPERATOR) => (e) => {
    setFilterField(field, operator)(e);
    setAutocompleteFilterField(field)(e);
  };

  const onLineClickHandler = (index: number) => (e) => {
    var senderElementName = e.target.tagName.toLowerCase();
    if (['input', 'image', 'svg', 'path', 'button'].includes(senderElementName))
      return;
    const invoiceId = invoices[index].id;
    checkInvoiceId(invoiceId);
  };

  const hasCheckedInvoices = checkInvoiceIds.length > 0;
  const hasFilters = (searchFilter.filter?.length ?? 0) > 0;
  const clearAll = () => {
    setInvoice('filters.invoices.filter', {
      ...getInvoice('filters.invoices.filter'),
      filter: [],
      orderBy: [],
      page: 1,
    });
    setInvoice('selecteds.invoices', []);
  };

  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownload = () => {
    setDownloadLoading(true);
    filterCompleteInvoice()
      .then((result) => {
        const invoices = result.data.filterInvoice.data;
        const taxNames: string[] = [
          ...new Set(
            invoices.flatMap(({ invoiceTaxes }) =>
              invoiceTaxes.map(({ tax: { name } }) => name),
            ),
          ),
        ];

        const data = [
          ...invoices.map(
            ({
              company,
              crt,
              date,
              invoiceNumber,
              invoiceQuantity,
              invoiceValue,
              product,
              realValue,
              status,
              totalCost,
              totalProfit,
              unitCostReal,
              unitCostTotal,
              unitProfit,
              unitSale,
              updatedAt,
              createdAt,
              observation,
              invoiceTaxes,
            }) => ({
              Produto: `${product.code} - ${product.resume}`,
              Companhia: company.name,
              Data: new Date(date).toLocaleString('pt-BR').split(',')[0],
              CRT: crt,
              Nota: invoiceNumber,
              'Valor NF': formatBRL(invoiceValue),
              'Qtd NF': invoiceQuantity,
              'Custo unit real': formatBRL(unitCostReal),
              'Valor real': formatBRL(realValue),
              Situação:
                situations.find(({ data }) => data === status)?.name ?? '',
              'Custo total': formatBRL(totalCost),
              'Custo unit': formatBRL(unitCostReal),
              'Custo unit total ': formatBRL(unitCostTotal),
              'Venda unit': formatBRL(unitSale),
              'Lucro unit': formatCompleteBRL(unitProfit),
              'Lucro total': formatCompleteBRL(totalProfit),
              ...taxNames.reduce((acc: any, name) => {
                acc[name] = formatBRL(
                  invoiceTaxes.find(({ tax: { name: n } }) => n === name)
                    ?.value ?? 0,
                );
                return acc;
              }, {}),
              Observação: observation,
              'Criado em': new Date(createdAt).toLocaleString('pt-BR'),
              'Atualizado em': new Date(updatedAt).toLocaleString('pt-BR'),
            }),
          ),
          {
            Produto: '',
            Companhia: '',
            Data: '',
            CRT: '',
            Nota: '',
            'Valor NF': formatBRL(reportInvoice.totalInvoiceValue),
            'Qtd NF': '',
            'Custo unit real': '',
            'Valor real': formatBRL(reportInvoice.totalRealValue),
            Situação: '',
            'Custo total': formatBRL(reportInvoice.totalTotalCost),
            'Custo unit': '',
            'Custo unit total ': '',
            'Venda unit': '',
            'Lucro unit': '',
            'Lucro total': formatCompleteBRL(reportInvoice.totalTotalProfit),
            ...taxNames.reduce((acc: any, name) => {
              acc[name] = formatBRL(
                reportInvoice.totalTaxes.find(({ tax }) => tax.name === name)
                  ?.total ?? 0,
              );
              return acc;
            }, {}),
            Observação: '',
            'Criado em': '',
            'Atualizado em': '',
          },
        ];

        const sheetname = 'Invoices';
        const workbook = jsonToExcel(data, sheetname);
        const filename = `invoices_${new Date().toLocaleString('pt-BR').replaceAll('/', '-').replaceAll(':', '-').replaceAll(', ', '_')}.xlsx`;
        downloadExcel(workbook, filename);
        enqueueSnackbar('Invoices baixadas com sucesso', {
          variant: 'success',
        });
      })
      .catch(() => {
        enqueueSnackbar('Houve uma falha ao baixar as invoices', {
          variant: 'error',
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  const isLock = !checkInvoiceIds.some(
    (checkInvoiceId) =>
      invoices.find(({ id }) => id === checkInvoiceId)?.locked,
  );

  const [lockLoading, setLockLoading] = useState(false);
  const lockInvoices = () => {
    setLockLoading(true);
    const length = checkInvoiceIds.length;
    updateLockInvoice(isLock)
      .then(() => {
        setInvoice(
          'filters.invoices.data.data',
          invoices.map((invoice) => ({
            ...invoice,
            locked: checkInvoiceIds.includes(invoice.id)
              ? isLock
              : invoice.locked,
          })),
        );

        filterInvoice();
        enqueueSnackbar(
          length === 1
            ? `Invoice ${isLock ? 'bloqueada' : 'desbloqueada'} com sucesso`
            : `Invoices ${isLock ? 'bloqueadas' : 'desbloqueadas'} com sucesso`,
          {
            variant: 'success',
          },
        );
      })
      .catch(() => {
        enqueueSnackbar(
          length === 1
            ? `Houve uma falha ao ${isLock ? 'bloquear' : 'desbloquear'} a invoice`
            : `Houve uma falha ao ${isLock ? 'bloquear' : 'desbloquear'} as invoices`,
          {
            variant: 'error',
          },
        );
      })
      .finally(() => {
        setLockLoading(false);
      });
  };
  const duplicateInvoice = ({
    id,
    product,
    date,
    crt,
    invoiceNumber,
    invoiceValue,
    invoiceQuantity,
    unitCostReal,
    realValue,
    status,
    totalCost,
    unitCostTotal,
    unitSale,
    unitProfit,
    totalProfit,
    company,
    group,
    invoiceTaxes,
    labeled,
    observation,
    locked,
  }: Invoice) => {
    setModal('single.createInvoice', true);
    setInvoice('forms.invoices.create', {
      product,
      date,
      crt,
      invoiceNumber,
      invoiceValue,
      invoiceQuantity,
      unitCostReal,
      realValue,
      status,
      totalCost,
      unitCostTotal,
      unitSale,
      unitProfit,
      totalProfit,
      observation: observation ?? '',
      company: {
        id: company.id,
        name: company.name,
      },
      invoiceTaxes: invoiceTaxes.map(({ id, value, tax }) => ({
        id,
        value,
        tax: {
          id: tax.id,
          name: tax.name,
          value: tax.value,
          description: tax.description,
        },
      })),
      locked,
      labeled,
      group: {
        id: group?.id ?? 0,
        number: group?.number ?? 0,
        name: group?.name ?? ''
      }
    });
  };

  const InvoiceControls = () => {
    return currentRoute.includes('/tools/exports/invoices') ? (
      <>
        <Can I="create" a="Invoice">
          {() => (
            <ButtonHeader
              className="button-header"
              onClick={() => {
                setModal('single.createInvoice', true);
              }}
            >
              <div
                style={{
                  marginRight: '20px',
                }}
              >
                <SVG.Icon.Plus fill="#364856" />
              </div>
              NOVA INVOICE
            </ButtonHeader>
          )}
        </Can>
        <Can I="update" a="Invoice" field="locked">
          {() =>
            checkInvoiceIds.length > 0 ? (
              <ButtonHeader
                className="button-header"
                onClick={lockInvoices}
                disabled={lockLoading}
              >
                <div
                  style={{
                    marginRight: '10px',
                  }}
                >
                  {lockLoading ? (
                    <SVG.Icon.Loader width={25} height={25} />
                  ) : isLock ? (
                    <SVG.Icon.Lock width={25} height={25} />
                  ) : (
                    <SVG.Icon.Unlock />
                  )}
                </div>
                {isLock ? 'BLOQUEAR' : 'DESBLOQUEAR'}
              </ButtonHeader>
            ) : null
          }
        </Can>
        <ControlContainer>
          {!downloadLoading ? (
            <Control onClick={handleDownload}>
              <SVG.Icon.Download color="#364856" width={24} height={24} />
            </Control>
          ) : (
            <Control>
              <SVG.Icon.Loader width={24} height={24} />
            </Control>
          )}

          <Can I="delete" a="Invoice">
            {() => (
              <Control
                active={hasCheckedInvoices}
                disabled={!hasCheckedInvoices}
                onClick={() => {
                  setModal('single.deleteInvoices', true);
                }}
              >
                <SVG.Icon.Trash
                  color={hasCheckedInvoices ? '#364856' : '#a7a7a7'}
                  width={24}
                  height={24}
                />
              </Control>
            )}
          </Can>

          <Control onClick={clearAll}>
            <SVG.Icon.SVGIconGrayBroom color="#364856" width={24} height={24} />
          </Control>
          <Control
            onClick={() => {
              setModal('single.filterInvoice', true);
            }}
          >
            <SVG.Icon.Filter width={24} height={24} borderOnly={!hasFilters} />
          </Control>
        </ControlContainer>
      </>
    ) : null;
  };

  const total = getInvoice('filters.invoices.data.meta.total');
  const currentPage = searchFilter.page ?? 1;
  const itemsPerPage = searchFilter.limit ?? 0;
  const setPage = (page: number) =>
    setSearchFilter({
      ...searchFilter,
      page,
    });

  const setItemsPerPage = (limit: number) =>
    setSearchFilter({
      ...searchFilter,
      page: 1,
      limit,
    });
  const TableControl = () => {
    return (
      <Table.Control
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalOfItems={total}
        setPage={setPage}
        setItemsPerPage={setItemsPerPage}
      />
    );
  };

  const locks = [
    { id: 0, name: 'DESBLOQUEADO', data: false },
    { id: 1, name: 'BLOQUEADO', data: true },
  ];

  const situations = [
    { id: 0, name: 'RECEBIDO', data: 'RECEIVED' },
    { id: 1, name: 'PENDENTE', data: 'PENDING' },
  ];

  const labels = [
    { id: 0, name: 'NÃO', data: false },
    { id: 1, name: 'SIM', data: true },
  ];

  const invoiceProducts = getInvoice('filters.invoiceProducts');
  const productItems = invoiceProducts.map((product, index) => ({
    id: index,
    name: `${product.code} - ${product.resume}`,
    data: product,
  }));

  const [productLoading, setProductLoading] = useState(false);

  const filterProducts = (searchQuery: string) => {
    setProductLoading(true);
    filterInvoiceProduct(searchQuery).finally(() => {
      setProductLoading(false);
    });
  };

  const invoiceGroups = groups.invoice;

  const invoiceGroupItems = invoiceGroups.map((group) => ({
    id: group.id,
    name: `${group.number} - ${group.name}`,
    data: group,
  }));


  return {
    invoices,
    checkInvoiceIds,
    setCheckInvoiceIds,
    allInvoiceChecks,
    invoiceCheckAll,
    checkInvoiceId,
    deleteInvoices,
    deleteInvoiceLoadings,
    InvoiceControls,
    editInvoice,
    searchFilter,
    getFilterField,
    setFilterField,
    getSortField,
    setSortField,
    getAutocompleteItems,
    setAutocompleteItems,
    setAutocompleteFilterField,
    setField,
    onLineClickHandler,
    setSearchFilter,
    filterInvoice,
    setInvoice,
    TableControl,
    getFormMessage,
    createInvoice,
    updateInvoice,
    getInvoice,
    setModal,
    validateForm,
    taxFilter,
    setTaxFilterField,
    filterSelectTaxes,
    selectCompanies,
    setCompanyFilterField,
    filterSelectCompanies,
    situations,
    labels,
    locks,
    filterInvoiceProduct,
    productItems,
    productLoading,
    filterProducts,
    reportInvoice,
    duplicateInvoice,
    filterGroupInvoice,
    groups,
    invoiceGroupItems,
    invoiceGroups,
    user,
    selectedGroupId,
  };
};

export default useInvoicesController;
